import { MetricoolOrganicPanel } from '../components/MetricoolOrganicPanel';
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Skeleton } from '../components/ui/skeleton';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../components/ui/table';
import { Download, Loader2, Clock, ArrowUp, ArrowDown, Minus } from 'lucide-react';
import { toast } from 'sonner';
import DashboardChart, { ReportChart } from '../components/DashboardChart';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const PLATFORM_META = {
  facebook: { color: '#1877F2', label: 'Facebook' },
  instagram: { color: '#E4405F', label: 'Instagram' },
  linkedin: { color: '#0A66C2', label: 'LinkedIn' },
  tiktok: { color: '#000000', label: 'TikTok' },
  youtube: { color: '#FF0000', label: 'YouTube' },
};

function MetricBox({ label, value, changePct, prefix = '', suffix = '' }) {
  const up = changePct > 0, down = changePct < 0;
  return (
    <div className="p-4 bg-white rounded-xl border">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{label}</div>
      <div className="text-xl font-semibold tabular-nums" style={{ color: '#1f2a4d', fontFamily: 'Space Grotesk, sans-serif' }}>
        {value == null ? '—' : `${prefix}${typeof value === 'number' ? value.toLocaleString() : value}${suffix}`}
      </div>
      <span className={`inline-flex items-center gap-0.5 text-[10px] font-semibold mt-1 ${up ? 'text-emerald-600' : down ? 'text-red-600' : 'text-slate-500'}`}>
        {up ? <ArrowUp size={10} /> : down ? <ArrowDown size={10} /> : <Minus size={10} />}
        {changePct == null ? 'Comparison unavailable' : `${Math.abs(changePct).toLocaleString(undefined, { maximumFractionDigits: 1 })}%`}
      </span>
    </div>
  );
}

export default function SharedReportPage({ token }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/shared/${token}`);
        if (res.status === 410) { setError('This share link has expired.'); return; }
        if (!res.ok) throw new Error('Report not found');
        setReport(await res.json());
      } catch (err) { setError(err.message); }
      finally { setLoading(false); }
    };
    fetchReport();
  }, [token]);

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/shared/${token}/pdf`);
      if (!res.ok) throw new Error('PDF generation failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${report?.client_name || 'report'}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error(err.message); }
    finally { setDownloadingPdf(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center">
      <Skeleton className="h-[400px] w-[800px] rounded-xl" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center">
      <Card className="max-w-md">
        <CardContent className="p-8 text-center">
          <Clock size={40} className="mx-auto text-muted-foreground/40 mb-4" />
          <h2 className="text-lg font-semibold mb-2">Link Unavailable</h2>
          <p className="text-sm text-muted-foreground">{error}</p>
        </CardContent>
      </Card>
    </div>
  );

  const ai = report?.ai_data || {};
  const metrics = report?.metrics || {};
  const platforms = metrics?.platforms || {};
  const org = metrics?.organic_summary || {};
  const dl = metrics?.date_labels || [];

  return (
    <div className="min-h-screen bg-[#fbfbfc]" data-testid="shared-report-view">
      {/* Top bar */}
      <div className="border-b bg-white sticky top-0 z-10">
        <div className="max-w-[1100px] mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-xl font-bold italic">
              <img src="/logos/freddy-script.png" alt="Freddy" className="h-7 object-contain" />
            </span>
            <Separator orientation="vertical" className="h-5" />
            <span className="text-xs text-muted-foreground">{report.client_name} — Marketing Report</span>
          </div>
          <Button variant="outline" size="sm" onClick={handleDownloadPdf} disabled={downloadingPdf} data-testid="pdf-export-button">
            {downloadingPdf ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Download size={14} className="mr-2" />}
            Download PDF
          </Button>
        </div>
      </div>

      <div className="max-w-[1100px] mx-auto px-6 py-8 space-y-10">
        {/* Cover */}
        <div className="text-center py-10 rounded-2xl" style={{ background: 'linear-gradient(135deg, #1f2a4d 0%, #2d3a5f 100%)' }}>
          <h1 className="text-3xl font-semibold tracking-tight text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            {report.client_name}
          </h1>
          <div className="w-12 h-0.5 bg-[#e01e25] mx-auto mt-3 mb-3 rounded-full" />
          <p className="text-sm text-white/60">Marketing Report</p>
          <p className="text-xs text-white/40 mt-2">
            {report.date_range?.start} to {report.date_range?.end}
            {report.comparison_range?.start && ` vs ${report.comparison_range.start} to ${report.comparison_range.end}`}
          </p>
        </div>

        {/* Executive Summary */}
        <Card className="rounded-2xl">
          <CardContent className="p-8">
            <h2 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#1f2a4d' }}>
              Executive Summary
            </h2>
            <p className="text-sm leading-relaxed text-muted-foreground">{ai.executive_summary}</p>
          </CardContent>
        </Card>

        {/* Overview Metrics */}
        {metrics.data_source==='metricool'&&<MetricoolOrganicPanel data={metrics.metricool} internal={false}/>}
        <div>
          <h2 className="text-base font-semibold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#1f2a4d' }}>
            Performance Overview
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <MetricBox label="Impressions" value={org.impressions?.current} changePct={org.impressions?.change_pct} />
            <MetricBox label="Engagements" value={org.engagements?.current} changePct={org.engagements?.change_pct} />
            <MetricBox label="Engagement Rate" value={org.engagement_rate?.current} suffix="%" changePct={org.engagement_rate?.change_pct} />
            <MetricBox label="Follower Growth" value={org.follower_growth?.current} prefix="+" changePct={org.follower_growth?.change_pct} />
          </div>

          {/* Overview Charts */}
          {dl.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DashboardChart
                title="Impressions by Platform"
                chartType="area"
                height={240}
                data={Object.fromEntries(Object.entries(platforms).filter(([,pd]) => pd.daily_impressions || pd.daily_views).map(([k,pd]) => [k, pd.daily_impressions || pd.daily_views]))}
                dataKeys={Object.keys(platforms).filter(k => platforms[k].daily_impressions || platforms[k].daily_views).map(k => ({ key: k, name: PLATFORM_META[k]?.label || k }))}
                dateLabels={dl}
              />
              <DashboardChart
                title="Engagements by Platform"
                chartType="area"
                height={240}
                data={Object.fromEntries(Object.entries(platforms).filter(([,pd]) => pd.daily_engagements).map(([k,pd]) => [k, pd.daily_engagements]))}
                dataKeys={Object.keys(platforms).filter(k => platforms[k].daily_engagements).map(k => ({ key: k, name: PLATFORM_META[k]?.label || k }))}
                dateLabels={dl}
              />
            </div>
          )}
        </div>

        {/* Highlights & Opportunities */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="rounded-2xl">
            <CardContent className="p-6">
              <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-500" /> Highlights
              </h3>
              {(ai.organic_highlights || []).map((h, i) => (
                <div key={i} className="flex items-start gap-2 p-2.5 bg-emerald-50/50 rounded-lg mb-2 border border-emerald-100/50">
                  <span className="text-xs font-bold text-emerald-700 mt-0.5">{i + 1}</span>
                  <p className="text-sm text-muted-foreground flex-1">{h}</p>
                </div>
              ))}
            </CardContent>
          </Card>
          <Card className="rounded-2xl">
            <CardContent className="p-6">
              <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-blue-500" /> Opportunities
              </h3>
              {(ai.organic_opportunities || []).map((o, i) => (
                <div key={i} className="flex items-start gap-2 p-2.5 bg-blue-50/50 rounded-lg mb-2 border border-blue-100/50">
                  <span className="text-xs font-bold text-blue-700 mt-0.5">{i + 1}</span>
                  <p className="text-sm text-muted-foreground flex-1">{o}</p>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Platform Sections */}
        {Object.entries(platforms).map(([pname, pdata]) => {
          const meta = PLATFORM_META[pname] || {};
          const aiP = (ai.platform_summaries || {})[pname] || {};
          const pdl = pdata.date_labels || dl;

          return (
            <Card key={pname} className="rounded-2xl overflow-hidden">
              <div className="px-6 py-4 flex items-center gap-3" style={{ background: meta.color || '#1f2a4d' }}>
                <div className="w-7 h-7 rounded-lg bg-white/20 flex items-center justify-center text-white font-bold text-xs">
                  {(meta.label || pname)[0].toUpperCase()}
                </div>
                <h3 className="text-base font-semibold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {meta.label || pname}
                </h3>
              </div>
              <CardContent className="p-6 space-y-5">
                {/* Metrics */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {pdata.impressions && <MetricBox label="Impressions" value={pdata.impressions.current} changePct={pdata.impressions.change_pct} />}
                  {pdata.engagements && <MetricBox label="Engagements" value={pdata.engagements.current} changePct={pdata.engagements.change_pct} />}
                  {pdata.engagement_rate && <MetricBox label="ER" value={pdata.engagement_rate.current} suffix="%" changePct={pdata.engagement_rate.change_pct} />}
                  {pdata.followers && <MetricBox label="Followers" value={pdata.followers.current} changePct={pdata.followers.change_pct} />}
                  {pdata.views && <MetricBox label="Views" value={pdata.views.current} changePct={pdata.views.change_pct} />}
                  {pdata.video_views && <MetricBox label="Video Views" value={pdata.video_views.current} changePct={pdata.video_views.change_pct} />}
                </div>

                {/* Chart */}
                {pdl.length > 0 && (pdata.daily_impressions || pdata.daily_views) && (
                  <ReportChart
                    title={`${meta.label || pname} Performance`}
                    data={{
                      ...(pdata.daily_impressions ? { [meta.label || pname]: pdata.daily_impressions } : {}),
                      ...(pdata.daily_views ? { Views: pdata.daily_views } : {}),
                      ...(pdata.daily_engagements ? { Engagements: pdata.daily_engagements } : {}),
                    }}
                    dataKeys={[
                      ...(pdata.daily_impressions ? [{ key: meta.label || pname, name: 'Impressions' }] : []),
                      ...(pdata.daily_views ? [{ key: 'Views', name: 'Views' }] : []),
                      ...(pdata.daily_engagements ? [{ key: 'Engagements', name: 'Engagements' }] : []),
                    ]}
                    dateLabels={pdl}
                  />
                )}

                {/* Highlights + Opportunities */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Highlights</h4>
                    {(aiP.highlights || []).map((h, i) => (
                      <div key={i} className="text-sm text-muted-foreground mb-1.5 flex items-start gap-1.5">
                        <span className="text-emerald-600 mt-0.5">•</span> {h}
                      </div>
                    ))}
                  </div>
                  <div>
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Opportunities</h4>
                    {(aiP.opportunities || []).map((o, i) => (
                      <div key={i} className="text-sm text-muted-foreground mb-1.5 flex items-start gap-1.5">
                        <span className="text-blue-600 mt-0.5">•</span> {o}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Top Posts */}
                {metrics.data_source!=='metricool' && pdata.top_posts && pdata.top_posts.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-3">Top {pdata.top_posts.length} Posts by Engagement</h4>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-8">#</TableHead>
                          <TableHead>Post</TableHead>
                          <TableHead className="text-center">Type</TableHead>
                          <TableHead className="text-right">Impr.</TableHead>
                          <TableHead className="text-right">Eng.</TableHead>
                          <TableHead className="text-right">ER</TableHead>
                          <TableHead className="text-right">Likes</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pdata.top_posts.map((post, idx) => (
                          <TableRow key={idx}>
                            <TableCell>
                              <span className="w-5 h-5 rounded-full text-white text-[10px] flex items-center justify-center font-bold" style={{ background: meta.color || '#1f2a4d' }}>
                                {post.rank}
                              </span>
                            </TableCell>
                            <TableCell>
                              {post.perma_link ? (
                                <a href={post.perma_link} target="_blank" rel="noopener noreferrer" className="text-sm font-medium hover:text-[#e01e25] hover:underline transition-colors">{post.title}</a>
                              ) : (
                                <div className="text-sm font-medium">{post.title}</div>
                              )}
                              <div className="text-xs text-muted-foreground">{post.date}</div>
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant="secondary" className="text-[10px]">{post.type}</Badge>
                            </TableCell>
                            <TableCell className="text-right tabular-nums text-sm">{post.impressions?.toLocaleString()}</TableCell>
                            <TableCell className="text-right tabular-nums text-sm font-semibold">{post.engagements?.toLocaleString()}</TableCell>
                            <TableCell className="text-right tabular-nums text-sm">{post.engagement_rate}%</TableCell>
                            <TableCell className="text-right tabular-nums text-sm">{post.likes?.toLocaleString()}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}

        {/* Next Phase */}
        <Card className="rounded-2xl">
          <CardContent className="p-8">
            <h2 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#1f2a4d' }}>
              Next Phase
            </h2>
            <p className="text-sm leading-relaxed text-muted-foreground">{ai.next_phase}</p>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center py-8">
          <img src="/logos/freddy-seal.png" alt="Freddy Media" className="w-16 h-16 mx-auto" />
          <p className="text-xs text-muted-foreground mt-1">www.freddymedia.com</p>
        </div>
      </div>
    </div>
  );
}
